/**
 * Pre-resolved Clearbit logo URLs for every tool slug.
 * Format: https://logo.clearbit.com/{root-domain}
 *
 * Rules:
 * - Always use the ROOT domain (no subdomains) — Clearbit resolves root only
 * - If Clearbit doesn't have a logo for a niche tool, the ToolCard falls back
 *   to a clean 2-letter initials badge automatically
 */
export const TOOL_LOGOS: Record<string, string> = {

  // ── AI Productivity ──────────────────────────────────────────────────────
  'chatgpt':               'https://logo.clearbit.com/openai.com',
  'claude':                'https://logo.clearbit.com/anthropic.com',
  'notion-ai':             'https://logo.clearbit.com/notion.so',
  'gamma':                 'https://logo.clearbit.com/gamma.app',
  'perplexity-ai':         'https://logo.clearbit.com/perplexity.ai',
  'otter-ai':              'https://logo.clearbit.com/otter.ai',
  'krisp':                 'https://logo.clearbit.com/krisp.ai',
  'tome':                  'https://logo.clearbit.com/tome.app',
  'fireflies-ai':          'https://logo.clearbit.com/fireflies.ai',
  'mem':                   'https://logo.clearbit.com/mem.ai',
  'superhuman':            'https://logo.clearbit.com/superhuman.com',
  'reclaim-ai':            'https://logo.clearbit.com/reclaim.ai',
  'gemini':                'https://logo.clearbit.com/google.com',
  'motion-ai':             'https://logo.clearbit.com/usemotion.com',
  'taskade-ai':            'https://logo.clearbit.com/taskade.com',
  'grok':                  'https://logo.clearbit.com/x.ai',
  'microsoft-copilot':     'https://logo.clearbit.com/microsoft.com',
  'character-ai':          'https://logo.clearbit.com/character.ai',
  'beautiful-ai':          'https://logo.clearbit.com/beautiful.ai',
  'poe':                   'https://logo.clearbit.com/poe.com',
  'cohere':                'https://logo.clearbit.com/cohere.com',
  'maximem':               'https://logo.clearbit.com/maximem.ai',
  'lemon':                 'https://logo.clearbit.com/lemon.ai',
  'dokubrain':             'https://logo.clearbit.com/dokubrain.com',
  'pulse-ai':              'https://logo.clearbit.com/pulseai.com',

  // ── AI Writing ───────────────────────────────────────────────────────────
  'jasper':                'https://logo.clearbit.com/jasper.ai',
  'copy-ai':               'https://logo.clearbit.com/copy.ai',
  'grammarly':             'https://logo.clearbit.com/grammarly.com',
  'writesonic':            'https://logo.clearbit.com/writesonic.com',
  'rytr':                  'https://logo.clearbit.com/rytr.me',
  'quillbot':              'https://logo.clearbit.com/quillbot.com',
  'wordtune':              'https://logo.clearbit.com/wordtune.com',
  'anyword':               'https://logo.clearbit.com/anyword.com',
  'sudowrite':             'https://logo.clearbit.com/sudowrite.com',
  'hypotenuse-ai':         'https://logo.clearbit.com/hypotenuse.ai',
  'longshot-ai':           'https://logo.clearbit.com/longshot.ai',
  'ink-for-all':           'https://logo.clearbit.com/inkforall.com',
  'frase':                 'https://logo.clearbit.com/frase.io',
  'peppertype-ai':         'https://logo.clearbit.com/peppertype.ai',
  'scalenut':              'https://logo.clearbit.com/scalenut.com',
  'narrato':               'https://logo.clearbit.com/narrato.io',
  'simplified':            'https://logo.clearbit.com/simplified.com',
  'contentbot':            'https://logo.clearbit.com/contentbot.ai',
  'lex':                   'https://logo.clearbit.com/lex.page',
  'cohesive-ai':           'https://logo.clearbit.com/cohesive.so',
  'neuroflash':            'https://logo.clearbit.com/neuroflash.com',
  'textcortex':            'https://logo.clearbit.com/textcortex.com',
  'describely':            'https://logo.clearbit.com/describely.ai',
  'growthbar':             'https://logo.clearbit.com/growthbarseo.com',
  'typeface':              'https://logo.clearbit.com/typeface.ai',
  'flompt':                'https://logo.clearbit.com/flompt.com',

  // ── AI Image Generators ──────────────────────────────────────────────────
  'dall-e-3':              'https://logo.clearbit.com/openai.com',
  'canva-ai':              'https://logo.clearbit.com/canva.com',
  'adobe-firefly':         'https://logo.clearbit.com/adobe.com',
  'ideogram':              'https://logo.clearbit.com/ideogram.ai',
  'leonardo-ai':           'https://logo.clearbit.com/leonardo.ai',
  'bing-image-creator':    'https://logo.clearbit.com/bing.com',
  'playground-ai':         'https://logo.clearbit.com/playground.com',
  'nightcafe':             'https://logo.clearbit.com/nightcafe.studio',
  'runway-gen-3':          'https://logo.clearbit.com/runwayml.com',
  'getimg-ai':             'https://logo.clearbit.com/getimg.ai',
  'craiyon':               'https://logo.clearbit.com/craiyon.com',
  'artbreeder':            'https://logo.clearbit.com/artbreeder.com',
  'pixlr-ai':              'https://logo.clearbit.com/pixlr.com',
  'fotor-ai':              'https://logo.clearbit.com/fotor.com',
  'krea-ai':               'https://logo.clearbit.com/krea.ai',
  'stability-ai':          'https://logo.clearbit.com/stability.ai',
  'picsart-ai':            'https://logo.clearbit.com/picsart.com',
  'photoroom':             'https://logo.clearbit.com/photoroom.com',
  'image-describer':       'https://logo.clearbit.com/imagedescriber.com',

  // ── AI Design ────────────────────────────────────────────────────────────
  'midjourney':            'https://logo.clearbit.com/midjourney.com',
  'jasper-art':            'https://logo.clearbit.com/jasper.ai',
  'looka':                 'https://logo.clearbit.com/looka.com',
  'uizard':                'https://logo.clearbit.com/uizard.io',
  'khroma':                'https://logo.clearbit.com/khroma.co',
  'magician':              'https://logo.clearbit.com/magician.design',
  'mirothinker':           'https://logo.clearbit.com/miro.com',
  'layerprep':             'https://logo.clearbit.com/layerprep.com',

  // ── AI Video ─────────────────────────────────────────────────────────────
  'runway':                'https://logo.clearbit.com/runwayml.com',
  'descript':              'https://logo.clearbit.com/descript.com',
  'pictory':               'https://logo.clearbit.com/pictory.ai',
  'synthesia':             'https://logo.clearbit.com/synthesia.io',
  'sora':                  'https://logo.clearbit.com/openai.com',
  'luma-ai':               'https://logo.clearbit.com/lumalabs.ai',
  'kling-ai':              'https://logo.clearbit.com/klingai.com',
  'heygen':                'https://logo.clearbit.com/heygen.com',
  'loom-ai':               'https://logo.clearbit.com/loom.com',
  'pika-labs':             'https://logo.clearbit.com/pika.art',
  'invideo-ai':            'https://logo.clearbit.com/invideo.io',
  'fliki':                 'https://logo.clearbit.com/fliki.ai',
  'opus-clip':             'https://logo.clearbit.com/opus.pro',
  'colossyan':             'https://logo.clearbit.com/colossyan.com',
  'veed-io':               'https://logo.clearbit.com/veed.io',
  'wondershare-filmora-ai':'https://logo.clearbit.com/wondershare.com',
  'kapwing':               'https://logo.clearbit.com/kapwing.com',
  'captions-ai':           'https://logo.clearbit.com/captions.ai',
  'genve-ai':              'https://logo.clearbit.com/genve.ai',

  // ── AI Voice & Music ─────────────────────────────────────────────────────
  'elevenlabs':            'https://logo.clearbit.com/elevenlabs.io',
  'murf-ai':               'https://logo.clearbit.com/murf.ai',
  'suno-ai':               'https://logo.clearbit.com/suno.com',
  'udio':                  'https://logo.clearbit.com/udio.com',
  'speechify':             'https://logo.clearbit.com/speechify.com',
  'resemble-ai':           'https://logo.clearbit.com/resemble.ai',
  'podcastle':             'https://logo.clearbit.com/podcastle.ai',
  'cleanvoice':            'https://logo.clearbit.com/cleanvoice.ai',

  // ── AI Coding ────────────────────────────────────────────────────────────
  'github-copilot':        'https://logo.clearbit.com/github.com',
  'cursor':                'https://logo.clearbit.com/cursor.com',
  'tabnine':               'https://logo.clearbit.com/tabnine.com',
  'replit-ai':             'https://logo.clearbit.com/replit.com',
  'lovable':               'https://logo.clearbit.com/lovable.dev',
  'bolt-new':              'https://logo.clearbit.com/stackblitz.com',
  'codeium':               'https://logo.clearbit.com/codeium.com',
  'amazon-codewhisperer':  'https://logo.clearbit.com/amazon.com',
  'windsurf':              'https://logo.clearbit.com/codeium.com',
  'pieces-for-developers': 'https://logo.clearbit.com/pieces.app',
  'devin-ai':              'https://logo.clearbit.com/cognition.ai',
  'shipsafe':              'https://logo.clearbit.com/shipsafe.dev',
  'insforge':              'https://logo.clearbit.com/insforge.io',

  // ── AI Website Builders ──────────────────────────────────────────────────
  'readdy':                'https://logo.clearbit.com/readdy.ai',
  'durable-ai':            'https://logo.clearbit.com/durable.co',
  'framer-ai':             'https://logo.clearbit.com/framer.com',
  'wix-ai':                'https://logo.clearbit.com/wix.com',
  '10web':                 'https://logo.clearbit.com/10web.io',
  'hostinger-ai':          'https://logo.clearbit.com/hostinger.com',
  'webflow-ai':            'https://logo.clearbit.com/webflow.com',
  'squarespace-ai':        'https://logo.clearbit.com/squarespace.com',
  'bubble':                'https://logo.clearbit.com/bubble.io',

  // ── AI Marketing ─────────────────────────────────────────────────────────
  'hubspot-ai':            'https://logo.clearbit.com/hubspot.com',
  'surfer-seo':            'https://logo.clearbit.com/surferseo.com',
  'semrush-ai':            'https://logo.clearbit.com/semrush.com',
  'mailchimp-ai':          'https://logo.clearbit.com/mailchimp.com',
  'hootsuite-ai':          'https://logo.clearbit.com/hootsuite.com',
  'adcreative-ai':         'https://logo.clearbit.com/adcreative.ai',
  'buffer-ai':             'https://logo.clearbit.com/buffer.com',
  'klaviyo-ai':            'https://logo.clearbit.com/klaviyo.com',
  'sprout-social-ai':      'https://logo.clearbit.com/sproutsocial.com',
  'ahrefs-ai':             'https://logo.clearbit.com/ahrefs.com',
  'activecampaign-ai':     'https://logo.clearbit.com/activecampaign.com',
  'manychat-ai':           'https://logo.clearbit.com/manychat.com',
  'predis-ai':             'https://logo.clearbit.com/predis.ai',
  'instantly-ai':          'https://logo.clearbit.com/instantly.ai',
  'chattee':               'https://logo.clearbit.com/chattee.io',
  'viewmetrics':           'https://logo.clearbit.com/viewmetrics.io',

  // ── AI Automation & Agents ───────────────────────────────────────────────
  'zapier-ai':             'https://logo.clearbit.com/zapier.com',
  'make':                  'https://logo.clearbit.com/make.com',
  'n8n':                   'https://logo.clearbit.com/n8n.io',
  'manus-ai':              'https://logo.clearbit.com/manus.im',
  'agentgpt':              'https://logo.clearbit.com/reworkd.ai',
  'relevance-ai':          'https://logo.clearbit.com/relevanceai.com',
  'activepieces':          'https://logo.clearbit.com/activepieces.com',
  'bardeen':               'https://logo.clearbit.com/bardeen.ai',
  'lindy-ai':              'https://logo.clearbit.com/lindy.ai',
  'pipedream':             'https://logo.clearbit.com/pipedream.com',
  'relay-app':             'https://logo.clearbit.com/relay.app',
  'voiceflow':             'https://logo.clearbit.com/voiceflow.com',
  'stack-ai':              'https://logo.clearbit.com/stack-ai.com',
  'botpress':              'https://logo.clearbit.com/botpress.com',
  'flowise':               'https://logo.clearbit.com/flowiseai.com',
  'crewai':                'https://logo.clearbit.com/crewai.com',
  'dify':                  'https://logo.clearbit.com/dify.ai',
  'vivgrid':               'https://logo.clearbit.com/vivgrid.com',
  'flowgent-ai':           'https://logo.clearbit.com/flowgent.ai',
};
